import { useExtensionUpdateNotice } from "@/components/features/ClubImport/useExtensionUpdateNotice";
import {
  type ClubImportSubsection,
  trackClubImportCta,
  trackImportClub,
} from "@/events/clubImportMixpanel";
import { setClubImportReturnTo } from "@/features/MyClub/EmptyClub/clubImportReturn";
import { useInitImport } from "@/hooks/useInitImport";
import {
  selectClubImportState,
  selectExtensionCheckReady,
} from "@/redux/clubImport/clubImportSlice";
import { setIsCurrentlyImporting } from "@/redux/globalState/globalStateSlice";
import { showDialog } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  getUserSelector,
  hasImportedBeforeSelector,
} from "@/redux/user/userSlice";
import { EA_WEB_APP_URL } from "@/settings";
import { safeGetItem } from "@/utils/safeStorage";
import {
  type ClubImportOrigin,
  noteClubImportIntent,
} from "@repo/shared/redux";
import { useCallback } from "react";
import { isMobileSafari } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

/** Matches the pause the old My Club button used before showing its overlay. */
const IMPORT_PANEL_DELAY_MS = 1000;

/**
 * Marks an import in flight and raises the global overlay, held back a beat so
 * the panel isn't opened straight into the EA tab taking the foreground.
 *
 * `subsection` names the screen the import started from, so the overlay's Done
 * is reported against it. Omitted by CTAs that have no screen of their own.
 */
export const raiseClubImportOverlay = (
  dispatch: AppDispatch,
  subsection?: ClubImportSubsection,
) => {
  dispatch(setIsCurrentlyImporting(true));
  setTimeout(
    () =>
      dispatch(
        showDialog({ modalType: "now-importing", data: { subsection } }),
      ),
    IMPORT_PANEL_DELAY_MS,
  );
};

/**
 * Starts the import and hands the user to the EA web app. Mechanics only — no
 * routing decision, no overlay, no progress state.
 *
 * Split out from `useStartClubImport` because the tutorial, the mobile guide
 * and the front-page sheet all need exactly this and nothing else: they *are*
 * the destination the routing rule sends people to, so applying the rule there
 * would bounce a first-timer from the tutorial to the tutorial. They also drive
 * their own inline progress UI rather than the global overlay.
 *
 * Resolves true when the EA app was opened.
 *
 * `origin` is required because this is where `import_club` is fired: every
 * launch in the app passes through here, so it is the one place the event can
 * be raised once and still name the surface the user came from. The tutorial
 * and the mobile guide are their own origin — they are the destination, not a
 * CTA someone clicked.
 */
export const useLaunchClubImport = (origin: ClubImportOrigin) => {
  const { user } = useSelector(getUserSelector);
  const initImport = useInitImport({ user });
  const { section, subsection } = origin;

  return useCallback(async (): Promise<boolean> => {
    /*
      iOS Safari blocks window.open once a call has been awaited, so the tab is
      claimed synchronously and pointed at the EA app afterwards. Previously
      only two of the five launch sites did this.
    */
    const newWindow = isMobileSafari ? window.open("", "_blank") : null;
    try {
      await initImport();
      if (newWindow) newWindow.location.href = EA_WEB_APP_URL;
      else window.open(EA_WEB_APP_URL, "_blank");
      trackImportClub({ section, subsection });
      return true;
    } catch {
      // initImport already explained the failure with an alert or a modal.
      if (newWindow) newWindow.close();
      return false;
    }
  }, [initImport, section, subsection]);
};

/**
 * The single "take me to the import" action behind every club-import CTA that
 * is not itself part of the tutorial.
 *
 * One rule, applied identically wherever the user clicks from:
 *
 *   Imported before, and able to import right now  ->  launch it, in place.
 *   Anything else                                  ->  route to the tutorial.
 *
 * "Able to import right now" is a supported browser (`selectExtensionCheckReady`)
 * whose extension check has not come back missing. Both halves matter: a
 * returning user on the wrong browser or without the extension needs the
 * tutorial as much as a first-timer does, and a first-timer is never dropped
 * straight into a flow they have not been shown.
 *
 * Before this, the call sites disagreed. My Club's button launched, the
 * empty-club dialog launched, the SBC sidebar launched with its own copy of the
 * sequence, and everything else navigated to `/my-club` and left the user to
 * find a button — so whether a click imported or merely moved you depended on
 * which screen you happened to be on.
 *
 * Mobile keeps its own destinations. Users who arrived from the iOS extension
 * app get `/import-mobile`; iOS Safari without the extension installed gets the
 * tutorial even when they have imported before, because there is nothing on the
 * device to talk to yet.
 *
 * The overlay is raised through ModalControllerV2 (see ClubImportOverlay), not
 * rendered by the caller — that is what lets any CTA launch rather than route.
 *
 * `origin` names the surface the CTA sits on and is recorded as the signup
 * intent before anything else happens. It is required rather than optional
 * because the routed branch ends at a login modal several screens away, where
 * the origin is no longer recoverable — a CTA that skipped it would silently
 * report its signups as a bare `login-required` from the tutorial. See
 * components/features/ClubImport/clubImportPrompt.ts.
 */
export const useStartClubImport = (origin: ClubImportOrigin) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const hasImportedBefore = useSelector(hasImportedBeforeSelector);
  const extensionCheckReady = useSelector(selectExtensionCheckReady);
  const { iosExtensionInstalled, extensionEnabledStatus } = useSelector(
    selectClubImportState,
  );
  const launchClubImport = useLaunchClubImport(origin);
  const showExtensionUpdateNotice = useExtensionUpdateNotice();
  const { section, subsection } = origin;

  /**
   * Resolves true when an import actually started, false when it routed.
   * `returnTo` is where the tutorial sends the user after a finished import
   * instead of My Club; ignored when the import launches in place.
   */
  return useCallback(
    async ({ returnTo }: { returnTo?: string } = {}): Promise<boolean> => {
      const proceed = async (): Promise<boolean> => {
        // Before the branch, not inside it: both outcomes can end in a signup, and
        // the launching one still routes to the tutorial when the extension turns
        // out to be missing. Same reason the CTA event goes here — the click is
        // intent, and most clicks route rather than import.
        noteClubImportIntent({ section, subsection });
        trackClubImportCta({ section, subsection });

        const toTutorial = (path: string) => {
          if (returnTo) setClubImportReturnTo(returnTo);
          navigate(path);
        };

        // Never imported, or nothing here can run an import: the routed flows own
        // both cases. Note `lastImportedAt` alone is not enough — it says they once
        // imported, not that this browser can. A missing extension routes too,
        // rather than launching into a "not installed" alert.
        if (
          !hasImportedBefore ||
          !extensionCheckReady ||
          extensionEnabledStatus === "disabled"
        ) {
          toTutorial(
            safeGetItem("FROM_EXTENSION_APP")
              ? "/import-mobile"
              : "/club-import-tutorial",
          );
          return false;
        }
        if (isMobileSafari && !iosExtensionInstalled) {
          toTutorial("/club-import-tutorial");
          return false;
        }

        /*
          Only the in-place launch is wrapped. The routed branches above end at
          the tutorial, which warns about an outdated extension itself — warning
          here as well would show it twice. On an older extension nothing runs
          until the user confirms, and the import starts from there, so undefined
          means "not started yet" rather than "failed".
        */
        return (
          (await showExtensionUpdateNotice(async () => {
            if (!(await launchClubImport())) return false;

            raiseClubImportOverlay(dispatch);
            return true;
          })) ?? false
        );
      };

      return proceed();
    },
    [
      dispatch,
      extensionCheckReady,
      extensionEnabledStatus,
      hasImportedBefore,
      iosExtensionInstalled,
      launchClubImport,
      navigate,
      origin,
      section,
      showExtensionUpdateNotice,
      subsection,
    ],
  );
};
